/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
import React, { useMemo } from 'react';
import {
    Badge,
    Inline,
    StyledModal,
    Text,
    VerticalDivider,
    useBreakpoint,
} from 'sarsaparilla';
import ReactTableWithLayout from 'shared-ui/components/ReactTableWithLayout/ReactTableWithLayout';
import { gnoment } from 'shared-ui/gnoment';
import { PassType } from '../../types/PassType';
import { getPrice } from '../utils';

interface RateRow {
    passType: string;
    rateEntries: { date: string; price: string }[];
}

interface PassRatesModalProps {
    passTypes?: PassType[];
    isOpen: boolean;
    onRequestClose: () => void;
}

export default function PassRatesModal({
    passTypes = [],
    isOpen,
    onRequestClose,
}: PassRatesModalProps) {
    const breakpoint = useBreakpoint();

    const inlineSpace = breakpoint?.isSmAndDown ? 'xxs' : 'sm';
    const textSize = breakpoint?.isSmAndDown ? 'xs' : 'md';
    //This is different because badge only has sizing md and up
    const badgeSize = breakpoint?.isSmAndDown ? 'md' : 'lg';

    const data = useMemo(() => {
        return passTypes
            .filter((passType) =>
                passType.periods?.some((period) =>
                    gnoment().isBefore(gnoment(period.start_date))
                )
            )
            .map((passType) => {
                const futurePeriods = (passType.periods ?? [])
                    .filter((period) => gnoment().isBefore(gnoment(period.start_date)))
                    .sort(
                        (a, b) =>
                            new Date(a.start_date ?? '').getTime() -
                            new Date(b.start_date ?? '').getTime()
                    );

                const rateEntries = futurePeriods.map((period) => ({
                    date: gnoment(period.start_date).format('MM/DD/YYYY'),
                    price: getPrice([period], period.start_date ?? '', true),
                }));

                return {
                    passType: passType.name,
                    rateEntries,
                };
            });
    }, [passTypes]);

    const renderPassType = (row: { original: RateRow }) => {
        return <Text size={textSize}>{row.original.passType}</Text>;
    };

    const renderRate = (row: { original: RateRow }) => {
        return (
            <>
                {row.original.rateEntries.map(
                    (entry: { date: string; price: string }, i: number) => (
                        <Inline space={inlineSpace} key={i}>
                            <Text size={textSize}>{entry.date}</Text>
                            <VerticalDivider />
                            <Badge appearance="info" size={badgeSize}>
                                {entry.price}
                            </Badge>
                        </Inline>
                    )
                )}
            </>
        );
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Pass Type',
                id: 'passType',
                width: 120,
                Cell: ({ row }: { row: { original: RateRow } }) => renderPassType(row),
            },
            {
                Header: 'Rate',
                id: 'rate',
                Cell: ({ row }: { row: { original: RateRow } }) => renderRate(row),
            },
        ],
        [textSize, badgeSize, inlineSpace]
    );

    return (
        <StyledModal
            size="lg"
            isOpen={isOpen}
            heading="Future Pass Rates"
            hasScrollableBody
            onRequestClose={onRequestClose}
        >
            <Text className="mb-2">See upcoming price changes</Text>
            <ReactTableWithLayout columns={columns} data={data} isStriped={false} />
        </StyledModal>
    );
}
